/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { colors } from "@farmlands/mui-theme";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { z } from "zod";

import Input from "../../../../components/Input";
import { ModalActions } from "../../../../components/Modal";
import { TestIds } from "../../../../testIds";
import { useEmailPhoneName } from "../../../../util/useEmailPhoneName";
import { isInvalidEmail } from "../../../../util/validateEmail";
import { isInvalidName } from "../../../../util/validateName";
import { isInvalidPhoneNumber } from "../../../../util/validatePhoneNumber";
import { selectCurrentFarmerAccount } from "../../../azure-b2c/storage";

import { useDrawdownRequestContext } from "./DrawdownRequestContext";

const CONTACT_FORM_KEYS = {
  name: "name",
  email: "email",
  phone: "phone",
  reference: "reference",
} as const;

const Step3 = () => {
  const account = useSelector(selectCurrentFarmerAccount);
  const requiresReference = !!account?.captureCustomerReference;
  const { email: prefillEmail, name: prefillName, phone: prefillPhone } = useEmailPhoneName();
  const { state, setState, salesAgreement } = useDrawdownRequestContext();

  const validationSchema = z.object({
    name: z
      .string()
      .min(1, "Name is required")
      .refine((value) => !isInvalidName(value), {
        message: "Please enter a valid name",
      }),
    email: z
      .string()
      .min(1, "Email address is required")
      .email("Please enter a valid email address")
      .refine((value) => !isInvalidEmail(value), {
        message: "Please enter a valid email address",
      }),
    phone: z
      .string()
      .min(1, "Phone number is required")
      .refine((value) => !isInvalidPhoneNumber(value), {
        message: "Please enter a valid phone number",
      }),
    reference: requiresReference
      ? z.string().max(60, "Maximum 60 characters allowed").min(1, "Reference is required")
      : z.string().max(60, "Maximum 60 characters allowed").optional(),
  });

  const {
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      name: state.contactDetails?.name ?? prefillName,
      email: state.contactDetails?.email ?? prefillEmail,
      phone: state.contactDetails?.phone ?? prefillPhone,
      reference: state.contactDetails?.reference ?? salesAgreement?.customerReference ?? "",
    },
    resolver: zodResolver(validationSchema),
  });

  const onSubmit = handleSubmit((values) => {
    setState({
      tabIndex: 3,
      contactDetails: values as {
        name: string;
        email: string;
        phone: string;
        reference?: string;
      },
    });
  });

  const nameValue = watch(CONTACT_FORM_KEYS.name);
  const emailValue = watch(CONTACT_FORM_KEYS.email);
  const phoneValue = watch(CONTACT_FORM_KEYS.phone);
  const referenceValue = watch(CONTACT_FORM_KEYS.reference);

  const isTfo = !!account?.hasTfoAccess;

  return (
    <form onSubmit={onSubmit}>
      <Typography variant="body2" color={colors.primary700} fontWeight="bold">
        {!isTfo ? "Enter your details" : "Enter customer details"}
      </Typography>
      <Typography variant="body2">
        {!isTfo ? "Confirm account details" : "Confirm the recipients details"}
      </Typography>

      <Stack gap={0.5} mt={1}>
        {!isTfo && (
          <Stack p={1} bgcolor={colors.primary100} borderRadius={1} my={2}>
            <Stack pl={1} gap={0.5} borderLeft={`4px solid ${colors.grey900}`}>
              <Typography color={colors.primary700} variant="body2" fontWeight="bold">
                {account?.accountName}
                <Typography color={colors.primary700} variant="body2" component="span" pl={1}>
                  ({account?.accountNumber})
                </Typography>
              </Typography>
              <Typography variant="body2" color={colors.primary700}>
                {nameValue}
              </Typography>
              <Typography variant="body2" color={colors.primary700}>
                {emailValue}
              </Typography>
            </Stack>
          </Stack>
        )}
        {isTfo && (
          <>
            <Input
              testId={TestIds.feat.drawdown.v2.step3.nameInput}
              label="Name"
              name={CONTACT_FORM_KEYS.name}
              type="text"
              disabled={!account?.hasTfoAccess}
              onChange={(e) =>
                setValue(CONTACT_FORM_KEYS.name, e.target.value, { shouldValidate: true })
              }
              value={nameValue}
              error={!!errors.name}
              errorMessage={errors.name?.message}
              required={!account?.hasTfoAccess}
            />
            <Input
              testId={TestIds.feat.drawdown.v2.step3.emailInput}
              label="Email address"
              name={CONTACT_FORM_KEYS.email}
              type="email"
              disabled={!account?.hasTfoAccess}
              onChange={(e) =>
                setValue(CONTACT_FORM_KEYS.email, e.target.value, { shouldValidate: true })
              }
              value={emailValue}
              required={!account?.hasTfoAccess}
              error={!!errors.email}
              errorMessage={errors.email?.message}
            />
          </>
        )}
        <Input
          testId={TestIds.feat.drawdown.v2.step3.phoneInput}
          label="Phone number"
          name={CONTACT_FORM_KEYS.phone}
          type="tel"
          onChange={(e) =>
            setValue(CONTACT_FORM_KEYS.phone, e.target.value, { shouldValidate: true })
          }
          value={phoneValue}
          required
          error={!!errors.phone}
          errorMessage={errors.phone?.message}
        />
        <Input
          testId={TestIds.feat.drawdown.v2.step3.referenceInput}
          label={account?.hasTfoAccess ? "Customer Reference" : "Your Reference"}
          placeholder="e.g. Your Purchase Order reference if required"
          name={CONTACT_FORM_KEYS.reference}
          type="text"
          required={requiresReference}
          onChange={(e) =>
            setValue(CONTACT_FORM_KEYS.reference, e.target.value, { shouldValidate: true })
          }
          value={referenceValue}
          inputBaseProps={{ maxLength: 60 }}
          error={!!errors.reference}
          helperText={
            errors.reference
              ? errors.reference.message
              : `${referenceValue?.length || 0}/60 characters`
          }
        />
      </Stack>

      <ModalActions showDivider>
        <Button
          key="next-step-btn"
          variant="contained"
          onClick={onSubmit}
          data-testid={TestIds.feat.drawdown.v2.step3.nextButton}
          sx={(theme) => ({
            [theme.breakpoints.down("sm")]: {
              width: "100%",
            },
          })}
        >
          Next
        </Button>
      </ModalActions>
    </form>
  );
};

export default Step3;
